import r2rServicesClient from 'common/utils/r2rServicesClient'

import { getInitialLanguageCode } from 'common/utils/context'
import {
  backendLogEventEndpoint,
  backendLogExitEndpoint,
  backendLogSearchEndpoint,
} from 'common/utils/paths'

const logAdWordsConversion = (trackingLabel, value) => {
  const isProduction = process.env.NODE_ENV === 'production'
  if (isProduction && typeof window.google_trackConversion === 'function') {
    window.google_trackConversion({
      google_conversion_id: 968290294,
      google_conversion_language: getInitialLanguageCode(),
      google_conversion_label: trackingLabel,
      google_conversion_value: value,
      google_conversion_currency: 'AUD',
      google_remarketing_only: false,
    })
  }
}

const logGaEvent = (category, action, label, value, fields = {}, nonInteraction = false) => {
  const params = {
    event_category: category,
    event_label: label,
    value,
    non_interaction: nonInteraction,
    ...fields,
  }

  const isProduction = process.env.NODE_ENV === 'production'
  if (isProduction) {
    if (window.gtag) {
      window.gtag('event', action, params)
    }
  } else {
    outputGaEventToConsole(category, action, label, value, fields)
  }
}

const logGaNonInteractionEvent = (category, action, label, value, fields) => {
  logGaEvent(category, action, label, value, fields, true)
}

const formatGaProductName = (origin, destination) => {
  return `${origin} -> ${destination}`
}

// TODO: double the GA event from the ECommerce event. With gtag there is
// no need to send the ECommerce data with the analytics event
const logGaAddToCartEvent = (product, category, action, label, value, fields = {}) => {
  logGaECommerceEvent([product], category, 'add_to_cart', {}, fields)
  logGaEvent(category, action, label, value, fields)
}

const logGaRemoveFromCartEvent = (product, category, action, label, value, fields = {}) => {
  logGaECommerceEvent([product], category, 'remove_from_cart', {}, fields)
  logGaEvent(category, action, label, value, fields)
}

const logGaCheckoutStepNonInteractionEvent =
    (products, details, category, action, label, value, fields = {}) => {
      logGaECommerceEvent(products, category, 'checkout_progress',
          details, fields, true)
      logGaNonInteractionEvent(category, action, label, value, fields)
    }

const logGaStartCheckoutNonInteractionEvent =
    (products, details, category, action, label, value, fields = {}) => {
      logGaECommerceEvent(products, category, 'begin_checkout', details, fields, true)
      logGaNonInteractionEvent(category, action, label, value, fields)
    }

const logGaTransactionNonInteractionEvent =
  (products, details, category, action, label, value, fields = {}) => {
    logGaECommerceEvent(products, category, 'purchase', details, fields, true)
    logGaNonInteractionEvent(category, action, label, value, fields)
  }

const logGaECommerceEvent = (products, ecCategory, ecAction,
    ecActionData, fields, nonInteraction = false) => {
  logGaEvent(ecCategory, ecAction, null, null, {
    ...fields,
    ...ecActionData,
    items: products,
  }, nonInteraction)
}

const logFunnelLanding = (landingOn, providers) => {
  logGaNonInteractionEvent(landingOn, 'Funnel:Landing', formatFunnelProviderLabel(providers))
}

const logFunnelExit = (exitFrom, exitTo, transportProviders) => {
  const label = formatFunnelProviderLabel(transportProviders)
  let action = 'Funnel:Exit'
  if (exitTo) {
    action += `:${exitTo}`
  }
  logGaNonInteractionEvent(exitFrom, action, label)
}

const logFunnelConversion = (convertOn, provider) => {
  logGaNonInteractionEvent(convertOn, 'Funnel:Convert', formatFunnelProviderLabel([provider]))
}

const formatFunnelProviderLabel = (transportProviders) => {
  let label = ''
  if (transportProviders && transportProviders.length > 0) {
    label = transportProviders.join('|')
    if (label.length > 0) {
      label = `|${label}|`
    }
  }
  return label
}

const outputGaEventToConsole = (category, action, label, value, fields) => {
  const isProduction = process.env.NODE_ENV === 'production'
  const ni = fields.non_interaction ? 'ni' : ''
  const boldStyle = isProduction ? 'font-weight:bold;' : ''
  const categoryStyle = `color:blue;${boldStyle}`
  const niStyle = `color:grey;font-size:9px;${boldStyle}`
  const actionStyle = `color:green;${boldStyle}`
  const labelStyle = `color:#48104d;${boldStyle}`
  const valueStyle = `color:#999;${boldStyle}`
  /* eslint-disable no-console */
  console.log(
    `» %c${category} %c${ni} %c${action || ''} %c${label || ''} %c${value || ''}`,
    categoryStyle,
    niStyle,
    actionStyle,
    labelStyle,
    valueStyle
  )
  if (Object.keys(fields).length !== 0) {
    console.log('  » fields: ', fields)
  }
  /* eslint-disable no-console */
}

const logBackendEvent = (type, sessionData, bookingData) => {
  const loopRepeatTiming = 5000
  const fetchLoop = setInterval(() => {
    // Loops through fetch until successful response
  r2rServicesClient(backendLogEventEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        failureType: type,
        sessionData: JSON.stringify(sessionData),
        bookingData: JSON.stringify(bookingData),
      },
    }).then(finishLogging) // No catch - so we swallow network errors from the fetch.
  }, loopRepeatTiming)

  const finishLogging = response => {
    if (response.status == 200) {
      clearInterval(fetchLoop)
    }
  }
}

const logBackendExit = (args) => {
  r2rServicesClient.post(backendLogExitEndpoint, args)
  .catch((error) => {
    /* eslint-disable no-console */
    console.log('error', error)
    /* eslint-enable no-console */
  })
}

const logBackendSearch = (args) => {
  r2rServicesClient.post(backendLogSearchEndpoint, args)
    .catch((error) => {
      /* eslint-disable no-console */
      console.log('error', error)
      /* eslint-enable no-console */
    })
}


export {
  formatGaProductName,
  logAdWordsConversion,
  logGaEvent,
  logGaNonInteractionEvent,
  logGaAddToCartEvent,
  logGaRemoveFromCartEvent,
  logGaStartCheckoutNonInteractionEvent,
  logGaCheckoutStepNonInteractionEvent,
  logGaTransactionNonInteractionEvent,
  logFunnelLanding,
  logFunnelExit,
  logFunnelConversion,
  logBackendEvent,
  logBackendExit,
  logBackendSearch,
}
